import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, MessageCircle, Mic, MicOff, Settings, X } from 'lucide-react';
import VoiceMeter from './VoiceMeter';
import { AGENT_VOICE_GROUPS, AGENT_VOICES } from './voices';

/**
 * The live-call pill for a bot conversation: the bot's face, a meter that
 * follows both voices, voice picker, type, mute and end. Shown at the top of
 * the chat it belongs to, and floated over any other screen while the call
 * carries on.
 */
export default function BotVoiceBar({
  micStream,
  remoteStream,
  connecting,
  muted,
  voice,
  face,
  onVoice,
  onType,
  onMute,
  onEnd,
  onOpen,
}: {
  micStream: MediaStream | null;
  remoteStream: MediaStream | null;
  connecting: boolean;
  muted: boolean;
  voice: string;
  face: ReactNode;
  onVoice: (voice: string) => void;
  onType: () => void;
  onMute: () => void;
  onEnd: () => void;
  /** Set when the bar floats outside the chat: tapping the face opens the conversation. */
  onOpen?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menuOpen]);
  const iconBtn = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-white active:scale-95';
  return (
    <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-neutral-950 py-1.5 pl-1.5 pr-1.5 shadow-2xl" role="status" aria-live="polite" aria-label={connecting ? 'Connecting voice' : 'Voice connected'}>
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        {onOpen ? (
          <button type="button" onClick={onOpen} className={`flex items-center justify-center rounded-full active:scale-95 ${connecting ? 'opacity-40' : ''}`} aria-label="Open the conversation" title="Open chat">
            {face}
          </button>
        ) : (
          <span className={connecting ? 'opacity-40' : undefined}>{face}</span>
        )}
        {connecting && (
          <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          </span>
        )}
      </span>
      {connecting ? (
        <span className="px-1 text-[13px] font-medium text-white">Connecting</span>
      ) : (
        <VoiceMeter micStream={micStream} remoteStream={remoteStream} muted={muted} />
      )}
      <div className="relative" ref={menuRef}>
        <button type="button" onClick={() => setMenuOpen((open) => !open)} className={iconBtn} aria-label="Voice" aria-expanded={menuOpen} title="Voice">
          <Settings className="h-4 w-4" />
        </button>
        {menuOpen && (
          <div className="absolute left-1/2 top-12 z-30 max-h-[28rem] w-48 -translate-x-1/2 overflow-y-auto rounded-2xl bg-neutral-950 p-1.5 shadow-2xl">
            {AGENT_VOICE_GROUPS.map((group) => (
              <div key={group.id} className="pb-1">
                <p className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">{group.label}</p>
                {AGENT_VOICES.filter((option) => option.group === group.id).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => { onVoice(option.id); setMenuOpen(false); }}
                    className={`flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left text-[13px] text-white ${option.id === voice ? 'bg-neutral-800' : 'hover:bg-neutral-800'}`}
                  >
                    <span>{option.label}{option.id === 'marin' ? ' · default' : ''}</span>
                    {option.id === voice && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <button type="button" onClick={onType} className={iconBtn} aria-label="Type a message" title="Type">
        <MessageCircle className="h-4 w-4" />
      </button>
      <button type="button" onClick={onMute} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white active:scale-95 ${muted ? 'bg-rose-500' : 'bg-neutral-800'}`} aria-label={muted ? 'Unmute microphone' : 'Mute microphone'} title={muted ? 'Unmute' : 'Mute'}>
        {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
      <button type="button" onClick={onEnd} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-500 text-white active:scale-95" aria-label="End voice conversation" title="End">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
